// src/oura/components/search/PagesCard.tsx
//
// The pages Google shows, and how many of the pages we crawled it shows at
// all: the indexation gap the crawl cannot see on its own.

import { FileTextIcon } from "lucide-react";
import type { AeoSearchPerformance } from "@service/aeo";
import { cn } from "../../lib/cn";
import { FoldCard, TYPE } from "@oura/ui";
import { TABLE } from "../client/staffScreen";
import { num, position, positionTone } from "./format";

/** The first 20, as before: the rest are the long tail of a handful of impressions each. */
const SHOWN = 20;

export function PagesCard({ search, isStaff }: { search: AeoSearchPerformance; isStaff?: boolean }) {
    const pages = search.pages ?? [];
    if (pages.length === 0) return null;
    const listed = Math.min(pages.length, SHOWN);
    // The rows are the top of a longer list: Google showed 14 pages, the
    // payload lists 8. The folded head and the line under it state one fact
    // ("Top 8 of 14"), where "8 pages" over "14 of 32 pages" read as two
    // counts of the same thing that disagree.
    const appeared = Math.max(search.pagesWithImpressions ?? 0, pages.length);
    const isTop = appeared > listed;
    const fact = isTop ? `Top ${num(listed)} of ${num(appeared)}` : `${num(listed)} ${listed === 1 ? "page" : "pages"}`;
    // Google can list pages the crawl never read (a page outside its reach),
    // and "40 of the 32 pages" would claim more than the whole: the share is
    // only stated while it is a share.
    const reach = search.pagesCrawled && appeared <= search.pagesCrawled
        ? isStaff
            ? `${num(appeared)} of ${num(search.pagesCrawled)} pages we crawled have ever appeared in search.${isTop ? ` The top ${num(listed)} are listed.` : ""}`
            : `${num(appeared)} of the ${num(search.pagesCrawled)} pages on your site show up on Google.${isTop ? ` These are the top ${num(listed)}.` : ""}`
        : isTop
            ? isStaff
                ? `${num(appeared)} pages have appeared in search. The top ${num(listed)} are listed.`
                : `${num(appeared)} pages on your site show up on Google. These are the top ${num(listed)}.`
            : null;
    return (
        <FoldCard
            id="pages"
            anchor="search-pages"
            defaultFolded
            title={isStaff ? "Pages earning search traffic" : "Pages that show up on Google"}
            icon={{ icon: FileTextIcon, area: "google" }}
            flush
            foldedFact={fact}
        >
            {reach && <p className={cn(TYPE.tc, "m-0 px-5 pb-3 text-gray-500")}>{reach}</p>}
            <div className={TABLE.scroll}>
                <table className="w-full min-w-max border-collapse">
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Page</th>
                            <th className={cn(TABLE.th, "w-px text-right")}>Times shown</th>
                            <th className={cn(TABLE.th, "w-px text-right")}>Visits</th>
                            <th className={cn(TABLE.th, "w-px text-right")}>Position</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pages.slice(0, SHOWN).map((p) => (
                            <tr key={p.url} className="transition-colors hover:bg-gray-50">
                                <td className={cn(TABLE.td, "max-w-lg")}>
                                    <span className={cn(TYPE.tb, "block truncate text-gray-900")} title={p.url}>{p.url}</span>
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>{num(p.impressions)}</td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>{num(p.clicks)}</td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-right font-medium tabular-nums", positionTone(p.position))}>{position(p.position)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </FoldCard>
    );
}
